import { LTLState } from "@taskpane/hooks/useLTLReducer";
import type { DraftForm } from "@src/load-agent/domain/shipmentDraft";
import type { LtlFormCapabilities, TmsProviderCapabilities } from "@src/types/api";

const REQUIRED = "This field is required";
const INVALID_TIME = "Enter a valid time";

function isBlank(s: string | undefined | null): boolean {
  return s == null || String(s).trim() === "";
}

function isValidHmTime(s: string | undefined | null): boolean {
  const t = s != null ? String(s).trim() : "";

  return t.length > 0 && /^([01]\d|2[0-3]):[0-5]\d$/.test(t);
}

export const BOL_DETAILS_VALIDATION_KEYS = [
  "originAddress",
  "originAddress2",
  "destinationAddress",
  "destinationAddress2",
  "shipperName",
  "shipperNumber",
  "pickupOpenTime",
  "pickupCloseTime",
] as const;
export type BolDetailsValidationForm = Pick<
  DraftForm,
  (typeof BOL_DETAILS_VALIDATION_KEYS)[number]
>;
export function stripBolDetailErrors(errors: Record<string, string>): Record<string, string> {
  const out = { ...errors };

  for (const k of BOL_DETAILS_VALIDATION_KEYS) delete out[k];
  return out;
}

function requireBolText(
  errors: Record<string, string>,
  flag: boolean | undefined,
  value: string | undefined,
  key: keyof DraftForm
) {
  if (flag && isBlank(value)) {
    errors[key] = REQUIRED;
  }
}

function requireBolTime(
  errors: Record<string, string>,
  required: boolean | undefined,
  value: string | undefined,
  key: "pickupOpenTime" | "pickupCloseTime"
) {
  if (!required) return;
  if (isBlank(value)) errors[key] = REQUIRED;
  else if (!isValidHmTime(value)) errors[key] = INVALID_TIME;
}

export function validateBolDetailsForCapabilities(
  form: BolDetailsValidationForm,
  caps: LtlFormCapabilities
): Record<string, string> {
  const errors: Record<string, string> = {};

  requireBolText(errors, caps.requiresOriginAddress, form.originAddress, "originAddress");
  requireBolText(errors, caps.requiresOriginAddress2, form.originAddress2, "originAddress2");
  requireBolText(
    errors,
    caps.requiresDestinationAddress,
    form.destinationAddress,
    "destinationAddress"
  );
  requireBolText(
    errors,
    caps.requiresDestinationAddress2,
    form.destinationAddress2,
    "destinationAddress2"
  );
  requireBolText(errors, caps.requiresShipperName, form.shipperName, "shipperName");
  requireBolText(errors, caps.requiresShipperNumber, form.shipperNumber, "shipperNumber");
  requireBolTime(errors, caps.requiresPickupOpenTime, form.pickupOpenTime, "pickupOpenTime");
  requireBolTime(errors, caps.requiresPickupCloseTime, form.pickupCloseTime, "pickupCloseTime");
  return errors;
}
export function validateLTL(
  state: LTLState,
  providerConfig: TmsProviderCapabilities,
  opts?: {
    includeBolDetails?: boolean;
  }
): Record<string, string> {
  const errors: Record<string, string> = {};
  const { form, items, shipmentMode, equipment } = state;
  const includeBolDetails = opts?.includeBolDetails ?? true;

  if (!form.originZip) errors.originZip = REQUIRED;
  if (!form.destinationZip) errors.destinationZip = REQUIRED;
  if (!form.pickupDate) errors.pickupDate = REQUIRED;
  const caps = providerConfig.ltlFormCapabilities;

  if (caps && includeBolDetails) {
    Object.assign(errors, validateBolDetailsForCapabilities(form, caps));
  }
  items.forEach((item: LTLState["items"][number], idx: number) => {
    if (!item.class) errors[`item${idx}_class`] = "Class is required";
    if (!item.weight || item.weight <= 0) {
      errors[`item${idx}_weight`] = "Weight must be greater than 0";
    }
    if (
      includeBolDetails &&
      caps?.requiresNmfcPerItem &&
      isBlank(item.nmfc != null ? String(item.nmfc) : "")
    ) {
      errors[`item${idx}_nmfc`] = "NMFC is required";
    }
  });
  if (providerConfig.requiresEquipmentForTruckload && shipmentMode === "Truckload" && !equipment) {
    errors.equipment = "Equipment is required for Truckload";
  }
  return errors;
}
